import React, {useRef, useEffect} from 'react';
import styles from '../../../styles/portfolio/projects/distributed_coutner.scss';
import {Note} from "./Note";
import {Link} from "./Link";
import {HighlightedText} from "./HighlightedText";
import {useLocation} from "react-router-dom";
import SyntaxHighlighter from 'react-syntax-highlighter';
import { androidstudio as syntaxStyle } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import {
    distributedCounterAggregationAgent,
    distributedCounterDockerComposeForFaust,
    distributedCounterDockerComposeForKafka,
    distributedCounterDockerfileForWorkers,
    distributedCounterFaustApp, distributedCounterLocalSetup,
    distributedCounterProducerLambda,
    distributedCounterProducerServerlessYml, distributedCounterSocketIoInReactComp, distributedCounterSocketService,
    processVideoIdentifiersAgent,
    socketIoServerBasics,
    socketIoServerConsumption,
    videoClickModel
} from "./code_snippets/CodeSnippets";
import {UnderConstruction} from "./UnderConstruction";

export const DistributedCounter = props => {
    const containerRef = useRef(null);
    const systemDesignRef = useRef(null);
    const introductionRef = useRef(null);
    const requirementsRef = useRef(null);
    const implementationRef = useRef(null);
    const deploymentRef = useRef(null);
    const demoRef = useRef(null);
    const dataIngestionImplementationRef = useRef(null);
    const streamProcessingRef = useRef(null);

    const scrollToSection = (elementRef) => {
        elementRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }

    function openInNewTab(url) {
        window.open(url, '_blank');
    }

    let {hash} = useLocation();
    let section = hash.replaceAll('#', '');
    useEffect(() => {
        switch (section) {
            case 'introduction':
                scrollToSection(introductionRef);
                break;
            case 'system-design':
                scrollToSection(systemDesignRef);
                break;
            case 'requirements':
                scrollToSection(requirementsRef);
                break;
            case 'implementation':
                scrollToSection(implementationRef);
                break;
            default:
                break;
        }
    }, [section]);

    return (
        <div className={styles.container} ref={containerRef}>
            <section id="introduction" ref={introductionRef}>
                <h1>Distributed Counter</h1>
                <span>
                    This is a PoC project that implements a low latency and high-throughput system capable of computing
                    real-time aggregations for many identifiers. <br/>
                    For the scope of this project, identifiers identify a video, whose only metadata are the video
                    title.
                    Aggregated counts (which could be seen as "video likes") are computed every 1', 5' and 10', but it
                    is fairly easy to configure the project in order to use different time intervals.
                    These aggregations are stored in three different kafka topics, from where they are consumed and
                    exposed through a websocket. <br/>
                    The frontend is receiving real time video likes per 1', 5' and 10' by subscribing to the above
                    mentioned websockets, and it is updating the corresponding view everytime that new data arrive.
                    In addition, frontend is capable of creating multiple requests per minute, that represent
                    a video likes, in order to easily demonstrate the functionality that we describe.
                    The following technologies are used:
                    <ul>
                        <li>
                            <b><Link text={'Faust'} url={'https://faust.readthedocs.io/'}/></b>: a library that enables us to do stream processing using pure python.
                        </li>
                        <li>
                            <b className={styles.link} onClick={() => openInNewTab('https://www.confluent.io/')}>
                                Confluent Kafka
                            </b>: a distributed event streaming platform based on
                            <span className={styles.link} onClick={() => openInNewTab('https://kafka.apache.org/')}
                            > Apache Kafka</span>, that provides our system
                            with low-latency and high-throughput capabilities.
                        </li>
                        <li>
                            <b><Link text={'RocksDB'} url={'https://rocksdb.org/'} /></b>: a fast in memory database that enables us to temporarily store aggregated data.
                        </li>
                        <li>
                            <b><Link text={'Socket.IO'} url={'https://socket.io/'} /></b>: a library that enables real-time, bidirectional and event-based communication between
                            a client (frontend) and a server (consumer of aggregated data from kafka topics).
                        </li>
                        <li>
                            <b><Link text={'React.js'} url={'https://reactjs.org/'} /></b>: a declarative js library used to build the frontend.
                        </li>
                    </ul>
                </span>
                <h2>Table of Contents</h2>
                <span>
                    <li><Link text={'Problem Statement & Requirements'} section={requirementsRef}/></li>
                    <li><Link text={'System Design'} section={systemDesignRef}/></li>
                    <li><Link text={'Implementation'} section={implementationRef}/></li>
                    <li><Link text={'Demo'} section={demoRef}/></li>
                    <li><Link text={'Deployment on AWS'} section={deploymentRef}/></li>
                </span>
            </section>
            <section id="requirements" ref={requirementsRef}>
                <h2>Problem Statement & Requirements</h2>
                <span>
                    <ul>
                        <li>The system should be able to aggregate events that represent video likes. Video likes
                            are grouped per identifier, which represents a unique video entity.
                        </li>
                        <li>The system should be able to compute the like aggregations per video, every one, five and
                            ten minutes.
                        </li>
                        <li>The system should be able to provide a server that allows a SocketIO client to:
                            <ul>
                                <li>Register itself into rooms in order to listen for specific video identifiers</li>
                                <li>Notified with new aggregated likes per 1', for every video identifier for which the
                                    client is concerned.
                                </li>
                                <li>Notified with new aggregated likes per 5', for every video identifier for which the
                                    client is concerned.
                                </li>
                                <li>Notified with new aggregated likes per 10', for every video identifier for which the
                                    client is concerned.
                                </li>
                            </ul>
                        </li>
                        <li>User should be able to see existing videos through a UI.</li>
                        <li>User should be able to determine the likes that will be sent per minute, for the next 10
                            minutes,
                            for each video. This should be done through the UI.
                        </li>
                        <li>User should be able to start the like-creation process through the UI.</li>
                        <li>User should be able to see the aggregated video likes being updated in real time in the UI,
                            for the latest 1, 5 and 10 minutes.
                        </li>
                    </ul>
                </span>
                <h3>Non-functional Requirements</h3>
                <span>
                    <ul>
                        <li><b>Low Latency</b> <br/> The system should be able to quickly respond to a producer that
                            creates video likes.
                        </li>
                        <li><b>High Throughput</b> <br/> The system should be able to handle a huge amount of like
                            creation events per second.
                        </li>
                        <li><b>Scalability</b> <br/> While this is a PoC, both the design and the tools that will be used
                            should allow for easily scaling to a big amount of video-identifiers. In addition the system
                            should be designed in a way that it handles spikes in the like-creation process.
                        </li>
                    </ul>
                    <Note
                        header={'Security Note'}
                        message={'SSL/TLS termination and other security concerns are not discussed or implemented' +
                        ' as part of this PoC. However they should definitely be taken into account before moving to production.'}
                    />
                </span>
                <h3>API Design</h3>
                <span>
                    Following the above requirements our system should implement the following API:
                    <ul>
                        <li>
                            <b>get_available_videos(limit: int)</b> <br/> Returns a list of lenght limit, with
                            metadata for all the existing available videos.
                            Since the focus is on stream processing, pagination is out of the scope.
                        </li>
                        <li>
                            <b>create_video_like(identifier: str)</b> <br/> Creates a new video-like event in our system.
                        </li>
                        <li>
                            <b>register_to_rooms(identifier_list: List[str])</b> <br/> Allows a client to register to
                            several rooms, identified by video-identifiers, for which it will "listen" for new events
                            created for those identifiers. Events represent aggregated likes for some time period.
                        </li>
                        <li>
                            <b>register_to_rooms(identifier_list: List[str])</b> <br/> Allows a client to register to
                            several rooms, identified by video-identifiers, for which it will "listen" for new events
                            created for those identifiers. Events represent aggregated likes for some time period.
                        </li>
                        <li>
                            <b>consume_1_min_aggregations(client_id: str)</b> <br/> Allows a specific client to "listen"
                            for <b>one</b> minute video-likes aggregations. When new 1' aggregations are computed for one or more
                            identifiers for which the client has been registered <i>(through register_to_rooms)</i> it gets
                            notified.
                        </li>
                        <li>
                            <b>consume_5_min_aggregations(client_id: str)</b> <br/> Allows a specific client to "listen"
                            for <b>five</b> minute video-likes aggregations. When new 5' aggregations are computed for one or more
                            identifiers for which the client has been registered it gets notified.
                        </li>
                        <li>
                            <b>consume_10_min_aggregations(client_id: str)</b> <br/> Allows a specific client to "listen"
                            for <b>ten</b> minute video-likes aggregations. When new 10' aggregations are computed for one or more
                            identifiers for which the client has been registered it gets notified.
                        </li>

                    </ul>
                </span>
            </section>
            <section id="system-design" ref={systemDesignRef}>
                <h2>System Design</h2>
                <span>
                    Let's start with a high level overview of our system: <br/>
                    <img className={styles.image} src={require('../../../resources/images/distributed_counter/high_level.png')} /> <br/>
                    Multiple users may create like-event for any video identifier, while all users should be notified
                    for the videos that they have subscribe (can be more than one) when a new aggregation is available
                    for them. Our design aims in "controlling" the data throughput through our system using queues and
                    consumers, whose consume rate is configurable: <br/>
                    <img className={styles.image} src={require('../../../resources/images/distributed_counter/high_level_simple.png')}/>
                    <br/>
                    The above high level architecture of our system implements the idea of throughput decrease: <br/>
                    <ul>
                        <li>Produces produce like events with a high throughput. Events are persisted into the ingestion queue.</li>
                        <li>A first set of consumers <i>(the blue ones)</i> are constantly consuming data from the ingestion queue.</li>
                        <li>The blue consumers keep track of the likes for an amount of video identifiers in their memory. At some predefined intervals
                        (in our case 1', 5', and 10') they report all aggregates per video-identifier to the appropriate queue (1_min_aggregations,
                        5_min_aggregations, and 10_min_aggregations). This way we achieve lower throughput in these queues.
                        </li>
                        <li>Another set of consumers <i>(the orange ones)</i> are constantly consuming data from the aggregations queues.</li>
                        <li>The orange consumers broadcast a message for any new aggregation of an identifier, to all SocketIO clients
                            that have been subscribed to the corresponding room. </li>
                    </ul>
                    If it was required, the last set of consumers could write the data to a database without worrying about overloading it,
                    since the throughput at that point of our system is lower than the ingestion throughput (and configurable).
                    <Note
                        header={'Fault-tolerance'}
                        message={'While the blue consumers may be seen as non-fault tolerant, the usage of RocksDB as an in memory ' +
                        'database backed up by a Write Ahead Log stored in Kafka solves this problem. More info later in these docs.'}
                    />
                    The following figure shows the full architecture that will be used: <br/>
                    <img className={styles.image} src={require('../../../resources/images/distributed_counter/high_level_full.png')}/>
                    <br/>
                    In the following section we will explain how this architecture is meeting both our functional and non-functional requirements.
                </span>

                <h3>Data ingestion</h3>
                <span>
                    Components for data ingestion: <br/>
                    <img className={styles.image} src={require('../../../resources/images/distributed_counter/ingestion.png')}/>
                    <br/>
                    Data ingestion is happening through a simple REST endpoint provided by the lambda function <HighlightedText text={'producer'}/>.
                    The lambda function is behind a load balancer (which is an <Link text={'ApiGateway'} url={'https://aws.amazon.com/api-gateway'}/> when we
                    deal with <Link text={'AWS lambdas'} url={'https://aws.amazon.com/lambda/'}/>). This <HighlightedText text={'producer LB'}/> enables
                    our system to scale regardless the amount on concurrent requests. <br/>
                    The lambda function must quickly store the event and then process the following event. For this reason
                    lambda stores the event into a queue topic (<HighlightedText text={'video_clicks'}/>) provided by a Kafka Cluster. Kafka provides low-latency
                    (due to its append-only nature) and fault-tolerance (through partition replication into different brokers).
                    In addition, in case of a temporary unavailability of the Kafka Cluster, the producer lambda should be able to retry sending the message
                    without causing a <Link text={'retry storm'} url={'https://faun.pub/how-to-avoid-retry-storms-in-distributed-systems-91bf34f43c7f'}/> (
                    Exponential Backoff and Jitter algorithms can be used for this reason).<br/>
                    Detailed explanation regarding the implementation of the above design can
                    be found <Link text={'here'} section={dataIngestionImplementationRef}/>.
                </span>
                <h3 ref={streamProcessingRef}>Stream Processing</h3>
                <span>
                    As we have seen in the above section, new like events are ingested into a kafka cluster, that is a set of
                    Kafka Brokers and stored into the <HighlightedText text={'video_clicks'}/> topic.
                    Kafka topics are split into Partitions which are replicated into different brokers to provide fault-tolerance.
                    In the next figure we focus on the internals of a single kafka broker: <br/>
                    <img className={styles.image} src={require('../../../resources/images/distributed_counter/stream_processing.png')}/>
                </span>
                <h4>Re-Partitioning</h4>
                <span>
                    While in a simple PoC like this project we have control over the producer, a more common scenario is that
                    we have limited control over ingested data. For such a scenario, we need to re-partition the data
                    into a new topic, <HighlightedText text={'group_by'}/>, using the video-identifier as the key. The repartitioning
                    is <b>necessary</b> in order to ensure that events regarding a specific identifier will always end up to the same consumer
                    (or consumer-group).
                    <Note
                        header={'Scaling using kafka'}
                        message={'Thanks to Kafka\'s consistent hashing we can add any number of' +
                        ' consumers to the new topic if we want to increase the speed of data-like consumption. ' +
                        'Consumers with the same group-id consume messages from the same partition, in parallel.'}
                    />
                    Using the repartitioning and a single consumer in the each consumer group we can ensure that events created for a specific
                    video identifier will always be consumed by the same consumer. Thus, we can safely compute aggregations for this identifier in a given
                    window of time, and store it in local memory. Upon time window expiration we will forward the aggregated counts per identifier in
                    another topic. In our very specific example the re-partitioned like events are aggregated into the memory for 1, 5, and 10 minutes and
                    then the aggregated counts are stored into three new topics:
                    <ul>
                        <li>count_aggregations_1_min</li>
                        <li>count_aggregations_5_min</li>
                        <li>count_aggregations_10_min</li>
                    </ul>
                    The aggregated results are computed by <HighlightedText text={'faust workers'}/>,
                    that is different machines running one or more faust applications, which consumes data from the <HighlightedText text={'group_by'}/> topic.
                    This way we achieve control of the messages' throughput to aggregation topics. In addition we can easily scale our solution,
                    since consuming new like-events from the <HighlightedText text={'group_by'}/> topic only requires adding/removing
                    new consumers (faust workers).
                </span>
                <h4>Local Storage & Fault tolerance</h4>
                <span>
                    In the above section we mentioned that data are stored <HighlightedText text={'in memory'}/> but we haven't
                    really explained how our system performs when one of the faust workers stops performing for a while, or fails permanently.
                    Our system is fault tolerant since faust uses <HighlightedText text={'RocksDB'}/> for in memory storage, where a kafka topic
                    is used as a <Link text={'WAL'} url={'https://en.wikipedia.org/wiki/Write-ahead_logging'}/> (Write Ahead Log). When a faust worker
                    fails, another one replaces him, and uses the WAL in order to continue the video-like counting from where the exact point
                    where the previous instance failed.
                </span>
                <h3>Real time events through Websockets</h3>
                <span>
                    In order to expose video-like aggregations in real time we use the following design:
                    <img className={styles.image} src={require('../../../resources/images/distributed_counter/data_exposure.png')}/> <br/>
                    We use the <HighlightedText text={'websockets LB'}/> in order to distribute the websocket connections among different consumers.
                    These consumers belong to the same group, thus they can consume the same messages independently. Consumers are using
                    the <Link text={'aiokafka'} url={'https://github.com/aio-libs/aiokafka'}/> library as well as
                    the <Link text={'python-socketio'} url={'https://python-socketio.readthedocs.io/en/latest/index.html'}/> in
                    order to push messages to clients, everytime that a new video-like aggregation arrives at the corresponding topic.
                    Each consumer consists of a SocketIO server that allows clients to register to specific <HighlightedText text={'rooms'}/>, identified
                    by video-identifiers. This way clients are notified only for aggregation of the specific videos, which concern them.
                </span>
                <h4>Websocket scaling</h4>
                <span>
                    In order to scale websockets using the SocketIO we need:
                    <ul>
                        <li>A Load-balancer that supports <Link text={'Sticky Sessions'} url={'https://socket.io/docs/v4/using-multiple-nodes/#sticky-load-balancing'}/></li>
                        <li>A Redis-cluster that will be used in order to <Link text={'manage sessions for the different socketIO instances'} url={'https://socket.io/docs/v4/redis-adapter/'}/>
                        </li>
                    </ul>
                    <Note
                        header={'Not Implemented'}
                        message={'Note that the code for this PoC includes neither the WebSocket LB nor the Redis cluster.'}
                        type={'warn'}
                    />
                </span>
                <h3>Frontend design</h3>
                <span>
                    A prototype of the desired UI that must be implemented by the frontend is shown in the figure bellow: <br/>
                    <img className={styles.image} src={require('../../../resources/images/distributed_counter/frontend.png')}/>
                    The frontend makes an initial GET request to a simple server, which responds with a list of video data
                    (in our example video data consist only of an identifier and the title).
                    Then, the UI can be used in order to determine the exact number of likes that will be sent (asynchronously)
                    every minute - the cumulative likes for the first, and second five minutes, as well as for the total ten minutes are
                    computed automatically. Once the user press the <HighlightedText text={'Send Likes'}/> button the frontend is making
                    the specified amount of concurrent requests for each video identifiers. These requests end up to the <HighlightedText text={'producer LB'}/>
                    and from there are ingested to our system.
                    Finally, frontend subscribes to the SockerIO server, and receives messages everytime that a new like-aggregation is ready, that is
                    every 1, 5, and 10 minutes. For every like-aggregation the corresponding number of likes is updated on the UI.
                </span>
            </section>
            <span>
            <section id="implementation" ref={implementationRef}>
                <h2>Implementation</h2>
                Implementation of the above design is split into the following projects:
                <ul>
                    <li><Link text={'distributed-counter'} url={'https://github.com/chrisbek/distributed-counter'}/></li>
                    Includes the docker-compose file for creating the <HighlightedText text={'Kafka Cluster'}/>,
                    containerized <HighlightedText text={'Faust consumers'}/>, and a simplified lambda that acts as the <HighlightedText text={'like producer'}/>.
                    <li><Link text={'kafka_consumer_websockets'} url={'https://github.com/chrisbek/kafka_consumer_websockets'}/></li>
                    Includes the implementation  & containerization of a kafka consumer that exposes aggregated video likes through websockets.
                    <li><Link text={'distributed-counter-frontend'} url={'https://github.com/chrisbek/distributed-counter-frontend'}/></li>
                    Includes the frontend code (written in React & compiled through Webpack) that implements the above mentioned UI.
                </ul>
                In the following sections we will describe and explain the most important part of the above projects. Finally we will also provide
                detailed instructions on how to setup and test the project locally, as well as code in order deploy on AWS.

                <h3 ref={dataIngestionImplementationRef}>Data ingestion - Implementation</h3>
                Implementation of the <HighlightedText text={'producer'}/> lambda function is pretty simple, all we need is
                the following <HighlightedText text={'serverless.yml'}/> file (only the most important parts of the file are shown):
                <SyntaxHighlighter language="yaml" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterProducerServerlessYml}
                </SyntaxHighlighter>

                Note that we have used the minimum amount of lambdas' memory size, and configured a small timeout. In a production like environment
                you may need to increase the timeout, since if the Kafka Cluster is not responding the producer should retry
                Since we are using AWS Lambda function, the API Gateway will provide us with the required load balancing capabilities.
                Then we also need to implement the <HighlightedText text={'src.producer.count_video_like'}/> function:
                <SyntaxHighlighter language="python" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterProducerLambda}
                </SyntaxHighlighter>
                For the Exponential Backoff and Jitter we can configure
                the <Link text={'KafkaProducer'} url={'https://kafka-python.readthedocs.io/en/master/apidoc/KafkaProducer.html'}/> accordingly.

                <h3>Stream Processing - Implementation</h3>
                In this section we will provide the most importand code snippets for the implementation of the design discussed in
                the <Link text={'Stream Processing'} section={streamProcessingRef}/>.

                <h4>Kafka Cluster</h4>
                First of all we need to create a minimalistic (single broker) Kafka Cluster, with the following docker-compose file:
                <SyntaxHighlighter language="yaml" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterDockerComposeForKafka}
                </SyntaxHighlighter>
                The stack is placed inside a bridge network which can be easily created with the command below:
                <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                    {'docker network create -d bridge --attachable main-net --subnet 172.30.0.0/16 --gateway 172.30.0.255'}
                </SyntaxHighlighter>
                We use a single kafka broker, but in a production like environment one should use at least 3 brokers and configure the quorum accordingly.
                Based on our <HighlightedText text={'KAFKA_ADVERTISED_LISTENERS'}/> configuration any service inside
                the <HighlightedText text={'main-net'}/> network can access the cluster at port <HighlightedText text={'9092'}/>, while
                services outside of the network must use the <HighlightedText text={'9094'}/> port.

                <h4>Faust Workers</h4>
                We use <Link text={'Faust'} url={'https://faust.readthedocs.io/'}/>, a Robinhood's library,
                that enables us to use pure python for stream processing.
                <Note
                    header={'Maintenance Note'}
                    message={'Instead of Robinhood\'s faust, the faust-streaming is used, which is an actively maintained fork of it.'}
                />
                Faust is implementing the <Link text={'Actor Model'} url={'https://en.wikipedia.org/wiki/Actor_model'}/>, where actors are
                named <HighlightedText text={'agents'}/> and one or more agents may run as part of the same application (
                <HighlightedText text={'worker'}/>) asynchronously. When using faust in production, workers are usually executed in different
                machines, but this is not obligatory. In our case, we will use two workers on different machines,
                which consume different video identifiers from the <HighlightedText text={'group_by'} topic/> and
                store in-memory the aggregation for the last 1', 5', and 10'. When a time-window closes, workers send the
                cumulative count to the corresponding aggregation topic and reset the counter. The docker-compose file for the workers
                in given bellow:
                <SyntaxHighlighter language="yaml" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterDockerComposeForFaust}
                </SyntaxHighlighter>
                One can easily observe that workers belong to the same network as the kafka cluster. This is usually the case when
                running faust consumers in the same private network as the cluster, but it is not obligatory. If we want to have workers
                on a different network all we have to do is to use the corresponding url configured
                by the <HighlightedText text={'KAFKA_ADVERTISED_LISTENERS'}/>. <br/>
                Additionally, we use a specific Dockerfile for the faust workers because we need to
                use <HighlightedText text={'RocksDB'}/>, thus we need a container that has both python (and dependencies),
                as well as RocksDB. The following Dockerfile can be used for such purpose:
                <SyntaxHighlighter language="docker" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterDockerfileForWorkers}
                </SyntaxHighlighter>
                Notice that we extend
                the <Link text={'yamitzky/docker-python-rocksdb'} url={'https://github.com/yamitzky/docker-python-rocksdb'}/> docker
                image, in order to have RocksDB with python.

                <h4>Faust Agents</h4>
                Inside a faust application (that is, a worker) multiple processes (actors) are executed in parallel, according to the actor model.
                Actors are named <HighlightedText text={'agents'}/> in faust, and data cannot be shared between them, unless an in-memory table is used.
                In our case we define the following agents:
                <ul>
                    <li>
                        <b>process_video_identifiers</b>: processes raw data from the <HighlightedText text={'video_clicks'}/> topic
                        and re-partitions them based on the video-identifier.
                    </li>
                    <li>
                        <b>count_video_identifiers</b>: processes video likes from the partitioned <HighlightedText text={'group_by'}/> topic,
                        and increases the count for every new message that it consumes. The hash table of the identifiers-counts is stored in the
                        in-memory table. Re-partitioning based on the video-identifier guarantees that the same identifier will always be processed
                        by the same worker.
                    </li>
                    <li>
                        <b>count_video_identifiers_5</b>: same as the <HighlightedText text={'count_video_identifiers'}/>, but this one increases
                        counts for the 5' table.
                    </li>
                    <li>
                        <b>count_video_identifiers_10</b>: same as the <HighlightedText text={'count_video_identifiers'}/>, but this one increases
                        counts for the 10' table.
                    </li>
                </ul>
                In the following code snippet we focus on 1' events consuming and aggregation, but the logic remains the same for the 5' and 10' aggregations. <br/>
                First we initialize a faust app:
                <SyntaxHighlighter language="python" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterFaustApp}
                </SyntaxHighlighter>
                Then we define the <HighlightedText text={'process_video_identifiers'}/> agent:
                <SyntaxHighlighter language="python" style={syntaxStyle} className={styles.codeContainer}>
                    {processVideoIdentifiersAgent}
                </SyntaxHighlighter>
                VideoClick is a <HighlightedText text={'Faust Record'}/> defined as:
                <SyntaxHighlighter language="python" style={syntaxStyle} className={styles.codeContainer}>
                    {videoClickModel}
                </SyntaxHighlighter>

                Now that data are re-partitioned we need an agent to create the one minute aggregations in-memory:
                <SyntaxHighlighter language="python" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterAggregationAgent}
                </SyntaxHighlighter>
                In the above code snippet we define an in-memory table <HighlightedText text={'video_counts_1_min'}/> which
                will act as a distributed hash table. Each worker has its own shard of the table, where he stores cumulative counts
                per video identifier.
                <Note
                    header={'Configuration Note'}
                    message={'Notice that the number of partitions defined for the in-memory table must much the number' +
                    'of partitions used by the `group_by` topic.'}
                />
                We also define an agent that consumes from the above created video_clicks_grouped_by_identifier topic (
                the <HighlightedText text={'group_by'}/> topic), creates new objects and stores them into the table.
                Finally, we define the <HighlightedText text={'process_one_minute_window'}/> as the function that will be
                triggered once the time-window closes. This function simply sends the aggregated data to another topic
                that holds aggregated data (<HighlightedText text={'count_aggregations_1_min'}/>).
                <Note
                    header={'Configuration Note\n'}
                    message={'Notice that the number of partitions between the ingestion topic (group_by) and the output' +
                    'topic (count_aggregations_1_min) may be configured to be different.'}
                />
                The full code for the above implementation can be found <Link text={'here'} url={'https://github.com/chrisbek/distributed-counter/blob/main/src/consumers.py'}/>.

                <h3>Websockets Server & Kafka Consumer - Implementation</h3>
                In this section we will go through the most important parts of a SocketIO server that consumes from
                the <HighlightedText text={'count_aggregations_1_min'}/> topic and pushes aggregations back to clients in real time.
                Let's start with the SocketIO server:
                <SyntaxHighlighter language="python" style={syntaxStyle} className={styles.codeContainer}>
                    {socketIoServerBasics}
                </SyntaxHighlighter>
                This is a really simplified (and not scalable) version of the SocketIO server. It provides clients with the
                ability to connect and register to rooms. Rooms are identified by video identifiers, and they will allow us
                to push data for a new video identifier only to those clients that are concerned about it.
                <Note
                    header={'Configuration Note\n'}
                    message={'If we use the current configuration SocketIO will establish the connection using long polling ' +
                    'and later it will upgrade the connection using websockets (if the client supports them). However we could ' +
                    'enforce the usage of websockets if we wish to.'}
                />
                Let's now add the
                logic for message consumption from the <HighlightedText text={'count_aggregations_1_min'}/> topic:
                <SyntaxHighlighter language="python" style={syntaxStyle} className={styles.codeContainer}>
                    {socketIoServerConsumption}
                </SyntaxHighlighter>
                Notice that we use the aiokafka which is the async version of python-kafka in order to avoid blocking
                the execution of the SocketIO server. Also notice that when a new aggregation is consumed we broadcast it
                only to those clients who have registered for the corresponding room. The room is specified by the video
                identifier. Clients can listen for new aggregations at the event named <HighlightedText text={'one_minute_aggregations_event_name'}/>.
                <Note
                    header={'Use the correct kafka bootstrap server\n'}
                    message={'The above code uses kafka.broker.1.local:9092 as the bootstrap server, which is the internal advertised url' +
                    ', that we defined in a previous docker-compose file. This config will work only if this server is in the same network as the' +
                    'Kafka Cluster. If that\'s not the case, then the localhost:9094 must be used.'}
                />
                The full code for the above implementation can be
                found <Link text={'here'} url={'https://github.com/chrisbek/kafka_consumer_websockets/blob/main/src/server.py'}/>.

                <h3>The Frontend part</h3>
                Since this PoC is focused on stream processing rather than the frontend implementation we will not deep dive into it.
                The full code for frontend can be found at
                <Link text={'distributed-counter-frontend'} url={'https://github.com/chrisbek/distributed-counter-frontend'}/> and
                instructions on how to setup are given in the following section. <br/>
                However, it is worth mentioning how to integrate SocketIO in a ReactJS project. The first thing we need to define
                is <HighlightedText text={'SocketService.js'}/>:
                <SyntaxHighlighter language="javascript" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterSocketService}
                </SyntaxHighlighter>
                The above code snippet demonstrates a js client capable of establishing a connection to our SocketIO server, register to
                rooms (<HighlightedText text={'register_to_rooms'}/>), and
                provide listeners to specific server events (<HighlightedText text={'add_handler'}/>).
                The <HighlightedText text={'transports: [\'websocket\']'}/> ensures that client will use the websocket protocol, instead of
                starting with long polling and then upgrading to websockets (the default behavior of SocketIO).
                Finally, we need to use the functions provided by <HighlightedText text={'SocketService'}/> into a react component:

                <SyntaxHighlighter language="javascript" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterSocketIoInReactComp}
                </SyntaxHighlighter>


                <h3>Local Setup</h3>
                In order to see the above implementation in action you may either checkout
                the <Link text={'Demo section'} section={demoRef}/> or test it in your local environment. Here are the
                instructions for local setup:

                <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                    {distributedCounterLocalSetup}
                </SyntaxHighlighter>
                For more information you may check the <HighlightedText text={'README.md'}/> of the projects
                listed <Link text={'here'} section={implementationRef}/>.

            </section>
            <section id="demo" ref={demoRef}>
                <h2>Demo</h2>
                <UnderConstruction />
            </section>
            <section id="deployment" ref={deploymentRef}>
                <h2>Deployment on AWS</h2>
                <UnderConstruction />
            </section>
            </span>
        </div>
    )
};
